import { Component } from '@theme/component';

/**
 * dm-sticky-cta — the fixed bottom bar.
 *
 * A pure SUBSCRIBER. It renders from the buy form's `snapshot` getter on every
 * dm:state / dm:gating-change / dm:cart-added event and never keeps its own copy of
 * that state (Design - SOPs/dm-personalized.md §Sticky CTA bar).
 *
 * It serves BOTH product templates and tells them apart by `snapshot.mode`:
 *   · personalized (<dm-personalizer>, state machine 142:41) — not-started · partial ·
 *     complete · loading · added. "Personalize first ↑" (outlined, 252:263) scrolls to the
 *     configurator.
 *   · standard (<dm-standard-form>) — complete · loading · added · soldout · unavailable.
 *     It mirrors the inline CTA: "Sold out" for a variant that exists but is out of stock,
 *     "Unavailable" only for a combination that does not exist. Neither is `disabled`
 *     (aria-disabled instead), so activating it still takes the customer to the form to
 *     change the option (qa-std-r1-06).
 *
 * CTA treatment (fix-plan §2.4, D2-4): Not Started / Partial use .dm-btn--outline with no
 * price in the label; every other state is .dm-btn--primary and the ready label carries
 * the price ("Add to cart — $24.00"), so the separate total is hidden and the price never
 * shows twice. The price sits in its own span: on phones the personalized bar drops it
 * from the button and leads the sub-line with it instead, so "{Name}'s {product}" keeps
 * its width (qa-pers-r1-02). Both templates show the variant's compare-at price beside a
 * ready CTA while it is on sale, and only from 750 up (qa-std-r1-07).
 *
 * Thumb (contract C-8): follows the chosen photo — dm:state `toyColorPhotoUrl` (the
 * size-specific toy photo at 120w) on personalized, the snapshot's `variantPhotoUrl` (the
 * variant's featured photo at 120w) on standard; the featured image otherwise.
 *
 * Visibility (qa-std-r1-08): it observes the form's own CTA row (data-hero-selector), not
 * the whole hero, so the bar appears as soon as the inline CTA slides under the sticky
 * header — no dead band over the trust row and the hero's bottom padding. The CTA counts
 * as gone only once it is ABOVE the visible area; still below the fold (a phone at page
 * load) counts as not yet reached. The target is resolved at runtime with a fallback to
 * the hero and a null guard (risk R8).
 *
 * Focus (qa-pers-r1-05, WCAG 2.4.3): when the CTA sends the customer to the form instead
 * of submitting, focus moves there too. The bar hides once the form is back in view and
 * would otherwise drop focus to <body>.
 */
/** Same hold as both inline CTAs (motion 206:6962), so the bar and the button agree. */
const ADDED_HOLD_MS = 2000;

/** Used when the layout has not published --header-height (layout/theme.liquid). */
const HEADER_FALLBACK_PX = 72;

class DmStickyCta extends Component {
  requiredRefs = ['bar', 'cta'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** The element whose departure shows the bar: the inline CTA row, else the hero. */
  /** @type {Element | null} */
  #target = null;

  /** Top root margin the current observer was built with (the sticky header's height). */
  #headerOffset = -1;
  #resizeFrame = 0;

  #heroVisible = true;
  #added = false;
  #addedTimer = 0;

  /** @type {string} */
  #originalTitle = '';

  /** The server-rendered featured image, restored whenever no toy photo applies. */
  #featuredSrc = '';

  /** Last label / sub-line written, so a scroll-driven render does not rebuild the DOM. */
  #labelKey = '';
  #metaKey = '';

  /**
   * Last `toyColorPhotoUrl` carried by a dm:state event. null until one arrives; '' when
   * the toy colour was cleared. A snapshot that exposes the field takes precedence.
   * @type {string | null}
   */
  #photoUrl = null;

  /** @type {Record<string, string> | null} variant id → formatted compare-at (on-sale variants only) */
  #compareAt = null;

  #onChange = (/** @type {Event} */ event) => {
    const detail = event instanceof CustomEvent ? event.detail : null;
    if (detail && typeof detail === 'object' && 'toyColorPhotoUrl' in detail) {
      this.#photoUrl = typeof detail.toyColorPhotoUrl === 'string' ? detail.toyColorPhotoUrl : '';
    }
    this.#render();
  };

  #onAdded = () => {
    this.#added = true;
    this.#render();
    window.clearTimeout(this.#addedTimer);
    this.#addedTimer = window.setTimeout(() => {
      this.#added = false;
      this.#render();
    }, ADDED_HOLD_MS);
  };

  /** The sticky header can change height across a breakpoint; rebuild the observer if so. */
  #onResize = () => {
    window.cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = window.requestAnimationFrame(() => this.#observe());
  };

  connectedCallback() {
    super.connectedCallback();

    this.#originalTitle = this.refs.title instanceof HTMLElement ? this.refs.title.textContent?.trim() || '' : '';
    this.#featuredSrc = this.refs.thumb instanceof HTMLImageElement ? this.refs.thumb.getAttribute('src') || '' : '';

    document.addEventListener('dm:state', this.#onChange);
    document.addEventListener('dm:gating-change', this.#onChange);
    document.addEventListener('dm:cart-added', this.#onAdded);
    this.refs.cta?.addEventListener('click', this.#onCta);

    this.#target = this.#resolveTarget();
    if (this.#target && typeof IntersectionObserver !== 'undefined') {
      this.#observe();
      window.addEventListener('resize', this.#onResize, { passive: true });
    } else {
      // Nothing to watch (section reordered away) — never show rather than misfire.
      this.#heroVisible = true;
    }

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('dm:state', this.#onChange);
    document.removeEventListener('dm:gating-change', this.#onChange);
    document.removeEventListener('dm:cart-added', this.#onAdded);
    this.refs.cta?.removeEventListener('click', this.#onCta);
    window.removeEventListener('resize', this.#onResize);
    window.cancelAnimationFrame(this.#resizeFrame);
    this.#observer?.disconnect();
    this.#observer = null;
    window.clearTimeout(this.#addedTimer);
  }

  /**
   * The inline CTA row named by data-hero-selector, else the hero. A selector that does
   * not parse is skipped rather than allowed to throw.
   */
  #resolveTarget() {
    for (const selector of [this.dataset.heroSelector, '.dm-hero']) {
      if (!selector) continue;
      try {
        const element = document.querySelector(selector);
        if (element) return element;
      } catch {
        // Invalid selector — fall through to the hero.
      }
    }
    return null;
  }

  /** The sticky header's height, published on <body> by layout/theme.liquid. */
  #headerHeight() {
    const value = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));
    if (!Number.isFinite(value) || value < 0) return HEADER_FALLBACK_PX;
    return Math.min(Math.round(value), 200);
  }

  /** (Re)build the observer with the header's height as the top root margin. */
  #observe() {
    const target = this.#target;
    if (!target || typeof IntersectionObserver === 'undefined') return;

    const offset = this.#headerHeight();
    if (this.#observer && offset === this.#headerOffset) return;
    this.#headerOffset = offset;
    this.#observer?.disconnect();

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Gone only once it has moved above the visible area (under the header). A target
          // still below the fold has simply not been reached yet.
          const top = entry.rootBounds ? entry.rootBounds.top : offset;
          this.#heroVisible = entry.isIntersecting || entry.boundingClientRect.bottom > top;
        }
        this.#render();
      },
      { rootMargin: `-${offset}px 0px 0px 0px`, threshold: 0 }
    );
    this.#observer.observe(target);
  }

  /**
   * The buy form on whichever product template is rendering — the personalized
   * configurator or the standard form. Both publish the same `snapshot` shape.
   */
  #controller() {
    const element = /** @type {any} */ (document.querySelector('dm-personalizer, dm-standard-form'));
    return element && 'snapshot' in element ? element : null;
  }

  #onCta = () => {
    const controller = this.#controller();
    if (!controller) return;

    const snap = controller.snapshot;
    if (snap.submitting) return;

    if (snap.canSubmit) {
      controller.submit();
      return;
    }

    // Personalized: take them to the configurator. Standard: a sold-out or non-existent
    // variant is the only reason canSubmit is false, and the form is where they change it.
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    controller.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
    this.#focusForm(controller);
  };

  /**
   * Move focus to where the CTA just sent the customer, without a second jump:
   *   · personalized → the visible step heading (tabindex=-1 in the step snippets);
   *   · standard → the selected option, so the radio group they need to change is focused;
   *   · otherwise → the form itself, made programmatically focusable.
   * @param {HTMLElement} controller
   */
  #focusForm(controller) {
    const candidates = [
      controller.querySelector('.dm-config__panel:not([hidden]) .dm-config__step-heading'),
      controller.querySelector('[data-dm-option-value]:checked'),
      controller.querySelector('[data-dm-option-value]:not([disabled])'),
    ];
    for (const candidate of candidates) {
      if (!(candidate instanceof HTMLElement)) continue;
      candidate.focus({ preventScroll: true });
      if (document.activeElement === candidate) return;
    }
    if (!controller.hasAttribute('tabindex')) controller.setAttribute('tabindex', '-1');
    controller.focus({ preventScroll: true });
  }

  #render() {
    const bar = this.refs.bar;
    const cta = this.refs.cta;
    const controller = this.#controller();
    if (!(bar instanceof HTMLElement) || !(cta instanceof HTMLButtonElement) || !controller) return;

    const snap = controller.snapshot;
    const section = this.closest('.dm-pdp-sticky-cta-section') || this;
    const standard = snap.mode === 'standard';

    /** @type {'added' | 'loading' | 'complete' | 'soldout' | 'unavailable' | 'partial' | 'not-started'} */
    let state;
    if (this.#added) state = 'added';
    else if (snap.submitting) state = 'loading';
    else if (snap.canSubmit) state = 'complete';
    // Standard mirrors the inline CTA: a real variant that is out of stock reads "Sold out";
    // "Unavailable" is kept for a combination that does not exist.
    else if (standard) state = snap.variantId && !snap.available ? 'soldout' : 'unavailable';
    else if (snap.petName || snap.variantId) state = 'partial';
    else state = 'not-started';

    bar.dataset.state = state;
    bar.dataset.mode = standard ? 'standard' : 'personalized';

    const visible = !this.#heroVisible;
    bar.dataset.visible = String(visible);
    bar.setAttribute('aria-hidden', String(!visible));
    /** @type {HTMLElement} */ (this).dataset.visible = String(visible);
    if (section instanceof HTMLElement) section.dataset.visible = String(visible);

    // CTA treatment: outlined while the personalization is incomplete (252:263, 82:5308),
    // the filled primary for everything else (252:277, 204:6674, 174:606).
    const outlined = !standard && (state === 'not-started' || state === 'partial');
    cta.classList.toggle('dm-btn--outline', outlined);
    cta.classList.toggle('dm-btn--primary', !outlined);

    // Copy per the states sheet (213:499) and the state machine (142:41): ready reads
    // "Add to cart — $X", the title becomes "{Name}'s {product}", and loading reads
    // "Sending to Workshop…". A standard product has no configuration to nudge toward,
    // so its incomplete states never appear.
    const incomplete = this.#label('incomplete', 'Personalize first ↑');
    const labels = {
      'not-started': incomplete,
      partial: incomplete,
      soldout: this.#label('soldout', 'Sold out'),
      unavailable: this.#label('unavailable', 'Unavailable'),
      complete: this.#label('complete', 'Add to cart'),
      loading: 'Adding…',
      // One word for the confirmation everywhere: the inline CTAs on both templates read it.
      added: this.#label('added', 'Added!'),
    };
    const price = state === 'complete' && snap.totalFormatted ? snap.totalFormatted : '';
    this.#writeLabel(cta, labels[state], price);

    // Sold out / Unavailable stay operable — they scroll to the form — so they are marked
    // inactive with aria-disabled rather than `disabled` (which would also drop focus).
    cta.disabled = state === 'loading';
    if (state === 'soldout' || state === 'unavailable') cta.setAttribute('aria-disabled', 'true');
    else cta.removeAttribute('aria-disabled');
    cta.setAttribute('aria-busy', String(state === 'loading'));

    const title = this.refs.title;
    if (title instanceof HTMLElement && this.#originalTitle) {
      let text = this.#originalTitle;
      if (state === 'loading' && !standard) text = 'Sending to Workshop…';
      else if (
        !standard &&
        (state === 'complete' || state === 'added') &&
        snap.petName &&
        !snap.nameIsInitials &&
        this.dataset.namedTitle
      ) {
        // data-named-title is the product's own short name, so this reads right on a blanket too.
        const name = snap.petName.charAt(0) + snap.petName.slice(1).toLowerCase();
        text = `${name}’s ${this.dataset.namedTitle}`;
      }
      if (title.textContent !== text) title.textContent = text;
    }

    const meta = this.refs.meta;
    if (meta instanceof HTMLElement) {
      let text;
      if (standard) {
        // The standard sub-line is the chosen variant ("Bubblegum · 4 ft").
        text = snap.optionsLabel || this.dataset.idleHint || '';
      } else {
        // The chosen size leads, so both templates name the variant in the bar
        // (logic-sticky-data-per-mode); the standard line is its option values.
        const parts = [];
        if (snap.variantTitle) parts.push(snap.variantTitle);
        if (snap.petName) parts.push(`“${snap.petName}”`);
        if (snap.toyColorLabel) parts.push(snap.toyColorLabel);
        if (snap.embroideryLabel) parts.push(snap.embroideryLabel);
        // Before anything is chosen, the drawn sub-line invites the action (82:5307).
        text = parts.length ? parts.join(' · ') : this.dataset.idleHint || '';
      }
      // Personalized ready state: on phones the price moves from the button to the head of
      // the sub-line (CSS shows this span ≤749 only), so the title keeps its width.
      // Both modes carry the price on the sub-line; CSS shows it only on phones, where the
      // button drops its own price so the product name is not squeezed to a few letters.
      this.#writeMeta(meta, price, text);
    }

    const total = this.refs.total;
    if (total instanceof HTMLElement) {
      // Only the personalized incomplete states show it (CSS, ≥750): the live total once a
      // variant is resolved, otherwise the product's base price. Standard never does — its
      // price lives on the CTA.
      const text = standard
        ? ''
        : snap.canSubmit || snap.variantId
          ? snap.totalFormatted
          : this.dataset.defaultPrice || '';
      if (total.textContent !== text) total.textContent = text;
    }

    const compare = this.refs.compare;
    if (compare instanceof HTMLElement) {
      // Both templates (lower-sticky-compare-missing-personalized). A strike price only makes
      // sense beside a live price: standard while the variant can be bought; personalized
      // once the CTA is ready (complete / loading / added) — the incomplete states show the
      // running total instead.
      const ready = state === 'complete' || state === 'loading' || state === 'added';
      const live = standard ? state !== 'unavailable' && state !== 'soldout' : ready;
      const text = live ? this.#compareFor(snap.variantId) : '';
      const value = this.refs.compareValue;
      if (value instanceof HTMLElement && value.textContent !== text) value.textContent = text;
      compare.hidden = !text;
    }

    const thumb = this.refs.thumb;
    if (thumb instanceof HTMLImageElement) {
      // Contract C-8: the thumb follows the chosen photo — the toy-colour photo on
      // personalized, the variant's featured photo on standard (the stage's photo) — and
      // falls back to the product's featured image.
      const toyPhoto = typeof snap.toyColorPhotoUrl === 'string' ? snap.toyColorPhotoUrl : this.#photoUrl;
      const photo = standard ? snap.variantPhotoUrl : toyPhoto;
      const src = photo || this.#featuredSrc;
      if (src && thumb.getAttribute('src') !== src) thumb.setAttribute('src', src);
    }
  }

  /**
   * "Add to cart<span class=dm-sticky__cta-price> — $24.00</span>" inside one wrapper span,
   * so the button's flex gap never lands between label and price. Text only — the label
   * can carry the customer's input, so nothing is ever parsed as markup.
   * @param {HTMLButtonElement} cta
   * @param {string} text
   * @param {string} price
   */
  #writeLabel(cta, text, price) {
    const key = `${text}\u0000${price}`;
    if (key === this.#labelKey && cta.querySelector('.dm-sticky__cta-label')) return;
    this.#labelKey = key;

    const label = document.createElement('span');
    label.className = 'dm-sticky__cta-label';
    label.append(text);
    if (price) {
      const tail = document.createElement('span');
      tail.className = 'dm-sticky__cta-price';
      tail.textContent = ` — ${price}`;
      label.append(tail);
    }
    cta.replaceChildren(label);
  }

  /**
   * Sub-line with an optional leading price ("$24.00 · “LUNA” · Yellow"). The separator
   * lives inside the price span so hiding the span leaves no stray "·". Text only (the pet
   * name is customer input).
   * @param {HTMLElement} meta
   * @param {string} price
   * @param {string} text
   */
  #writeMeta(meta, price, text) {
    const key = `${price}\u0000${text}`;
    if (key === this.#metaKey) return;
    this.#metaKey = key;

    /** @type {(Node | string)[]} */
    const nodes = [];
    if (price) {
      const lead = document.createElement('span');
      lead.className = 'dm-sticky__meta-price';
      lead.textContent = text ? `${price} · ` : price;
      nodes.push(lead);
    }
    if (text) nodes.push(text);
    meta.replaceChildren(...nodes);
  }

  /**
   * Formatted compare-at price for a variant, or '' when it is not on sale. Reads the
   * server-rendered map once; a malformed map means no strike price, never a guess.
   * @param {string | number | undefined} variantId
   */
  #compareFor(variantId) {
    if (!variantId) return '';
    if (this.#compareAt === null) {
      this.#compareAt = {};
      const source = this.refs.compareAt;
      if (source instanceof HTMLScriptElement && source.textContent) {
        try {
          const parsed = JSON.parse(source.textContent);
          if (parsed && typeof parsed === 'object') this.#compareAt = parsed;
        } catch (error) {
          console.error('[dm-sticky-cta] Compare-at map is not valid JSON.', error);
        }
      }
    }
    const text = this.#compareAt[String(variantId)];
    return typeof text === 'string' ? text : '';
  }

  /**
   * @param {string} key
   * @param {string} fallback
   */
  #label(key, fallback) {
    return this.dataset[`${key}Label`] || fallback;
  }
}

if (!customElements.get('dm-sticky-cta')) {
  customElements.define('dm-sticky-cta', DmStickyCta);
}
